import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Store, LogOut, ScrollText, LayoutList, Users, ShieldCheck, Palette } from "lucide-react";

export default function TopBar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/store/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const nav = [
    { to: "/", label: "Workspace", icon: LayoutList },
    { to: "/history", label: "History", icon: ScrollText },
  ];
  if (user?.role === "admin") nav.push({ to: "/users", label: "Users", icon: Users });
  if (user?.role === "admin") nav.push({ to: "/settings", label: "Settings", icon: Palette });

  return (
    <header
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md"
      data-testid="top-bar"
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-6 px-6 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900 text-white">
            <Store className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <div className="font-head text-sm font-600 text-slate-900">SKU Quote</div>
            <div className="text-[11px] text-slate-500">
              {status?.store_domain || "Lightspeed"}
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-1">
          {nav.map((n) => {
            const active = location.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {status?.connected ? (
            <Badge
              variant="outline"
              className="hidden gap-1.5 border-green-200 bg-green-50 text-green-700 sm:flex"
              data-testid="store-connected"
            >
              <ShieldCheck className="h-3 w-3" />
              {status.outlet_count} store{status.outlet_count === 1 ? "" : "s"}
            </Badge>
          ) : null}
          <div className="hidden text-right leading-tight sm:block">
            <div className="text-sm font-500 text-slate-900" data-testid="current-user">
              {user?.name}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              {user?.role}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={logout}
            data-testid="logout-btn"
            className="gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </Button>
        </div>
      </div>
    </header>
  );
}
